import React, { createContext, useContext, useReducer, useEffect, useRef, useState } from 'react';
import { AppState, Asset, Bucket, Transaction, PriceHistory, CashFlowEvent, Liability, LiabilityRepayment, AssetClassMeta, MoneyTransaction, TxNode, AssetHolding } from '../types';
import { loadStateFromCloud, saveStateToCloud, deleteFundFromCloud, getWorkspaceId, getUserProfile, completeOnboarding, addMoneyTransactionToCloud, updateMoneyTransactionInCloud, deleteMoneyTransactionFromCloud, saveHolding, deleteHolding } from '../utils/storage';
import { INITIAL_DATA, DEFAULT_ASSET_CLASSES } from '../utils/initialData';
import { computeWalletBalance, GLOBAL_WALLET_ID, CASH_WALLET_ASSET_ID } from '../utils/calculations';
import { v4 } from '../utils/uuid';
import { SplashScreen } from '../components/SplashScreen';
import { OnboardingFlow } from '../components/OnboardingFlow';

type Action =
  | { type: 'SET_STATE'; payload: AppState }
  // Assets
  | { type: 'ADD_ASSET'; payload: Omit<Asset, 'id'> & { id?: string } }
  | { type: 'UPDATE_ASSET'; payload: Asset }
  | { type: 'DELETE_ASSET'; payload: string }
  | { type: 'SELL_ASSET'; payload: { assetId: string; quantity: number; price: number; date: string; notes: string } }
  // Prices
  | { type: 'UPDATE_PRICE'; payload: { assetId: string; price: number; source: 'manual' | 'ai-snippet' } }
  | { type: 'BULK_UPDATE_PRICES'; payload: { code: string; price: number }[] }
  // Buckets
  | { type: 'ADD_BUCKET'; payload: Omit<Bucket, 'id'> }
  | { type: 'UPDATE_BUCKET'; payload: Bucket }
  | { type: 'DELETE_BUCKET'; payload: string }
  // Transactions
  | { type: 'ADD_TRANSACTION'; payload: Omit<Transaction, 'id'> }
  // Cash flow events
  | { type: 'ADD_CASH_FLOW'; payload: Omit<CashFlowEvent, 'id'> }
  | { type: 'DELETE_CASH_FLOW'; payload: string }
  // Liabilities
  | { type: 'ADD_LIABILITY'; payload: Omit<Liability, 'id'> }
  | { type: 'UPDATE_LIABILITY'; payload: Liability }
  | { type: 'DELETE_LIABILITY'; payload: string }
  // Repayments
  | { type: 'ADD_REPAYMENT'; payload: Omit<LiabilityRepayment, 'id'> }
  | { type: 'DELETE_REPAYMENT'; payload: string }
  // Monthly expenses
  | { type: 'SET_MONTHLY_EXPENSE'; payload: { year: number; month: number; amount: number; notes: string } }
  | { type: 'DELETE_MONTHLY_EXPENSE'; payload: { year: number; month: number } }
  // Asset Classes (Master Data)
  | { type: 'ADD_ASSET_CLASS';      payload: AssetClassMeta }
  | { type: 'UPDATE_ASSET_CLASS';   payload: AssetClassMeta }
  | { type: 'DELETE_ASSET_CLASS';   payload: string }
  // Promote slug class → UUID (đảm bảo sync được lên DB)
  | { type: 'PROMOTE_ASSET_CLASS';  payload: { oldId: string; newClass: AssetClassMeta } }
  // Money Transactions (MoBu Flow)
  | { type: 'ADD_MONEY_TX';    payload: MoneyTransaction }
  | { type: 'UPDATE_MONEY_TX'; payload: MoneyTransaction }
  | { type: 'DELETE_MONEY_TX'; payload: string }
  | { type: 'SET_MONEY_TXS';   payload: MoneyTransaction[] }
  // Holdings (vị thế trong composite assets)
  | { type: 'SET_HOLDINGS';            payload: AssetHolding[] }
  | { type: 'ADD_HOLDING';             payload: AssetHolding }
  | { type: 'UPDATE_HOLDING';          payload: AssetHolding }
  | { type: 'DELETE_HOLDING';          payload: string }
  | { type: 'ROLLUP_COMPOSITE_ASSET';  payload: { assetId: string } };

function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'SET_STATE': return action.payload;

    case 'ADD_ASSET': {
      const newAsset: Asset = { ...action.payload, id: action.payload.id ?? v4() };
      const tx: Transaction = {
        id: v4(), assetId: newAsset.id, assetCode: newAsset.code, assetName: newAsset.name,
        type: 'BUY', date: newAsset.purchaseDate,
        quantity: newAsset.quantity, price: newAsset.costPrice,
        totalValue: newAsset.costPrice * newAsset.quantity, notes: 'Mua ban đầu',
      };
      return { ...state, assets: [...state.assets, newAsset], transactions: [...state.transactions, tx] };
    }

    case 'UPDATE_ASSET':
      return { ...state, assets: state.assets.map(a => a.id === action.payload.id ? action.payload : a) };

    case 'DELETE_ASSET':
      return { ...state, assets: state.assets.filter(a => a.id !== action.payload) };

    case 'SELL_ASSET': {
      const { assetId, quantity, price, date, notes } = action.payload;
      const asset = state.assets.find(a => a.id === assetId);
      if (!asset) return state;
      const realizedGain = (price - asset.costPrice) * quantity;
      const tx: Transaction = {
        id: v4(), assetId, assetCode: asset.code, assetName: asset.name,
        type: 'SELL', date, quantity, price,
        totalValue: price * quantity, realizedGain, notes,
      };
      const newQty = asset.quantity - quantity;
      const updatedAssets = newQty <= 0
        ? state.assets.filter(a => a.id !== assetId)
        : state.assets.map(a => a.id === assetId ? { ...a, quantity: newQty } : a);
      return { ...state, assets: updatedAssets, transactions: [...state.transactions, tx] };
    }

    case 'UPDATE_PRICE': {
      const { assetId, price, source } = action.payload;
      const ph: PriceHistory = { id: v4(), assetId, date: new Date().toISOString(), price, source };
      return {
        ...state,
        assets: state.assets.map(a => a.id === assetId ? { ...a, currentPrice: price } : a),
        priceHistory: [...state.priceHistory, ph],
      };
    }

    case 'BULK_UPDATE_PRICES': {
      const now = new Date().toISOString();
      const newPH: PriceHistory[] = [];
      const newAssets = state.assets.map(a => {
        const u = action.payload.find(x => x.code.toUpperCase() === a.code.toUpperCase());
        if (u) { newPH.push({ id: v4(), assetId: a.id, date: now, price: u.price, source: 'ai-snippet' }); return { ...a, currentPrice: u.price }; }
        return a;
      });
      return { ...state, assets: newAssets, priceHistory: [...state.priceHistory, ...newPH] };
    }

    case 'ADD_BUCKET': return { ...state, buckets: [...state.buckets, { ...action.payload, id: v4() }] };
    case 'UPDATE_BUCKET': return { ...state, buckets: state.buckets.map(b => b.id === action.payload.id ? action.payload : b) };
    case 'DELETE_BUCKET': {
      if (action.payload === 'ruong-chua') return state;
      return {
        ...state,
        buckets: state.buckets.filter(b => b.id !== action.payload),
        assets: state.assets.map(a => a.bucketId === action.payload ? { ...a, bucketId: 'ruong-chua' } : a),
      };
    }

    case 'ADD_TRANSACTION': return { ...state, transactions: [...state.transactions, { ...action.payload, id: v4() }] };
    case 'ADD_CASH_FLOW':   return { ...state, cashFlowEvents: [...state.cashFlowEvents, { ...action.payload, id: v4() }] };
    case 'DELETE_CASH_FLOW': return { ...state, cashFlowEvents: state.cashFlowEvents.filter(e => e.id !== action.payload) };

    case 'ADD_LIABILITY': {
      const newLiability: Liability = {
        ...action.payload,
        id: v4(),
        outstandingBalance: action.payload.originalBalance,
      };
      return { ...state, liabilities: [...(state.liabilities || []), newLiability] };
    }
    case 'UPDATE_LIABILITY': {
      // Recompute outstandingBalance from repayments
      const totalPaid = (state.repayments || [])
        .filter(r => r.liabilityId === action.payload.id)
        .reduce((s, r) => s + r.principalPaid, 0);
      const updated = { ...action.payload, outstandingBalance: Math.max(0, action.payload.originalBalance - totalPaid) };
      return { ...state, liabilities: (state.liabilities || []).map(l => l.id === updated.id ? updated : l) };
    }
    case 'DELETE_LIABILITY':
      return {
        ...state,
        liabilities: (state.liabilities || []).filter(l => l.id !== action.payload),
        repayments: (state.repayments || []).filter(r => r.liabilityId !== action.payload),
      };

    case 'ADD_REPAYMENT': {
      const newRepayment: LiabilityRepayment = { ...action.payload, id: v4() };
      const allRepayments = [...(state.repayments || []), newRepayment];
      const liabilities = (state.liabilities || []).map(l => {
        const totalPaid = allRepayments
          .filter(r => r.liabilityId === l.id)
          .reduce((s, r) => s + r.principalPaid, 0);
        return { ...l, outstandingBalance: Math.max(0, l.originalBalance - totalPaid) };
      });
      return { ...state, repayments: allRepayments, liabilities };
    }
    case 'DELETE_REPAYMENT': {
      const allRepayments = (state.repayments || []).filter(r => r.id !== action.payload);
      const liabilities = (state.liabilities || []).map(l => {
        const totalPaid = allRepayments
          .filter(r => r.liabilityId === l.id)
          .reduce((s, r) => s + r.principalPaid, 0);
        return { ...l, outstandingBalance: Math.max(0, l.originalBalance - totalPaid) };
      });
      return { ...state, repayments: allRepayments, liabilities };
    }

    case 'SET_MONTHLY_EXPENSE': {
      const { year, month, amount, notes } = action.payload;
      const exists = (state.monthlyExpenses || []).find(e => e.year === year && e.month === month);
      if (exists) {
        return { ...state, monthlyExpenses: state.monthlyExpenses.map(e => e.year === year && e.month === month ? { ...e, amount, notes } : e) };
      }
      return { ...state, monthlyExpenses: [...(state.monthlyExpenses || []), { id: v4(), year, month, amount, notes }] };
    }
    case 'DELETE_MONTHLY_EXPENSE':
      return { ...state, monthlyExpenses: (state.monthlyExpenses || []).filter(e => !(e.year === action.payload.year && e.month === action.payload.month)) };

    case 'ADD_ASSET_CLASS':
      return { ...state, assetClasses: [...(state.assetClasses || []), action.payload] };
    case 'UPDATE_ASSET_CLASS':
      return { ...state, assetClasses: (state.assetClasses || []).map(c => c.id === action.payload.id ? action.payload : c) };
    case 'DELETE_ASSET_CLASS':
      return { ...state, assetClasses: (state.assetClasses || []).filter(c => c.id !== action.payload) };
    case 'PROMOTE_ASSET_CLASS': {
      const { oldId, newClass } = action.payload;
      // Thay thế slug class bằng UUID version trong danh sách lớp tài sản
      const assetClasses = (state.assetClasses || [])
        .filter(c => c.id !== oldId)
        .concat([newClass]);
      // Cập nhật tất cả tài sản đang dùng slug cũ sang UUID mới
      const assets = state.assets.map(a =>
        a.assetClassId === oldId
          ? { ...a, assetClassId: newClass.id, assetClass: newClass.name }
          : a
      );
      return { ...state, assetClasses, assets };
    }

    case 'ADD_MONEY_TX':
      return { ...state, moneyTransactions: [action.payload, ...(state.moneyTransactions || [])] };
    case 'UPDATE_MONEY_TX':
      return { ...state, moneyTransactions: (state.moneyTransactions || []).map(t => t.id === action.payload.id ? action.payload : t) };
    case 'DELETE_MONEY_TX':
      return { ...state, moneyTransactions: (state.moneyTransactions || []).filter(t => t.id !== action.payload) };
    case 'SET_MONEY_TXS':
      return { ...state, moneyTransactions: action.payload };

    case 'SET_HOLDINGS':
      return { ...state, holdings: action.payload };

    case 'ADD_HOLDING':
      return { ...state, holdings: [...(state.holdings || []), action.payload] };

    case 'UPDATE_HOLDING':
      return {
        ...state,
        holdings: (state.holdings || []).map(h =>
          h.id === action.payload.id ? action.payload : h
        ),
      };

    case 'DELETE_HOLDING':
      return {
        ...state,
        holdings: (state.holdings || []).filter(h => h.id !== action.payload),
      };

    case 'ROLLUP_COMPOSITE_ASSET': {
      // Client-side rollup: tính lại value của parent asset từ holdings trong memory.
      // DB trigger cũng làm tương tự server-side — cái này để UI phản hồi ngay lập tức.
      const { assetId } = action.payload;
      const assetHoldings = (state.holdings || []).filter(h => h.assetId === assetId);
      const totalValue    = assetHoldings.reduce((s, h) => s + h.quantity * h.marketPrice, 0);
      const totalCost     = assetHoldings.reduce((s, h) => s + h.quantity * h.costPrice,  0);
      return {
        ...state,
        assets: state.assets.map(a =>
          a.id === assetId
            ? { ...a, currentPrice: totalValue, costPrice: totalCost, quantity: 1 }
            : a
        ),
      };
    }

    default: return state;
  }
}

function migrateState(s: AppState): AppState {
  // Chuẩn hoá: đổi ID cũ 'unallocated' → 'ruong-chua'; giữ nguyên 'ruong-chua' (là quỹ thực trong DB)
  const normalizedBuckets = s.buckets
    .filter(b => b.id !== 'unallocated')
    .map(b => {
      if ((b as any).targetAllocation !== undefined && (b as any).targetValue === undefined) {
        return { ...b, targetValue: 0 };
      }
      return b;
    });

  // Đảm bảo 'ruong-chua' luôn có mặt (là quỹ gốc chứa tài sản chưa phân bổ)
  const hasRuongChua = normalizedBuckets.some(b => b.id === 'ruong-chua');
  const buckets = hasRuongChua ? normalizedBuckets : [
    { id: 'ruong-chua', name: 'Danh mục Chung', description: 'Danh mục mặc định chứa tài sản chưa được phân bổ vào danh mục cụ thể', targetValue: 0, color: '#64748b', icon: 'inbox' },
    ...normalizedBuckets,
  ];

  // Chuẩn hoá bucketId của assets — null hoặc 'unallocated' → 'ruong-chua'
  const assets = s.assets.map(a => {
    const bid = (a as any).bucketId;
    if ((a as any).bucketIds !== undefined && bid === undefined) {
      const ids = (a as any).bucketIds as string[];
      return { ...a, bucketId: ids.length > 0 ? ids[0] : 'ruong-chua' };
    }
    if (!bid || bid === 'unallocated') return { ...a, bucketId: 'ruong-chua' };
    return a;
  });

  // ── Backfill from_node / to_node → chuẩn hoá lên Global Wallet ──────────
  const GLOBAL = '__global__';
  const moneyTransactions = (s.moneyTransactions || []).map((tx): MoneyTransaction => {
    // Skip nếu đã migrate đủ: TRANSFER giữ per-fund, còn lại phải dùng __global__
    // Ngoại lệ: "Giao dịch gốc" phải có from_node=external (không phải wallet)
    if (tx.from_node && tx.to_node) {
      if (tx.type === 'TRANSFER') return tx;
      const isOrigin = tx.reason === 'Giao dịch gốc';
      const fromOk = isOrigin
        ? tx.from_node.kind === 'external'
        : (tx.from_node.kind !== 'wallet' || tx.from_node.fund_id === GLOBAL);
      const toOk = tx.to_node.kind !== 'wallet' || tx.to_node.fund_id === GLOBAL;
      if (fromOk && toOk) return tx;
    }

    let from_node: TxNode | undefined;
    let to_node:   TxNode | undefined;

    switch (tx.type) {
      case 'BUY':
        // "Giao dịch gốc" = vốn ban đầu từ nguồn ngoài, không trừ ví chung
        from_node = tx.reason === 'Giao dịch gốc'
          ? { kind: 'external' }
          : { kind: 'wallet', fund_id: GLOBAL };
        to_node = tx.asset_id ? { kind: 'asset', asset_id: tx.asset_id } : { kind: 'external' };
        break;
      case 'SELL':
        from_node = tx.asset_id ? { kind: 'asset', asset_id: tx.asset_id } : { kind: 'external' };
        to_node   = { kind: 'wallet', fund_id: GLOBAL };
        break;
      case 'INCOME':
        from_node = tx.asset_id ? { kind: 'asset', asset_id: tx.asset_id } : { kind: 'external' };
        to_node   = { kind: 'wallet', fund_id: GLOBAL };
        break;
      case 'DEPOSIT':
        from_node = { kind: 'external' };
        to_node   = { kind: 'wallet', fund_id: GLOBAL };
        break;
      case 'WITHDRAW':
        from_node = { kind: 'wallet', fund_id: GLOBAL };
        to_node   = { kind: 'external' };
        break;
      case 'TRANSFER':
        // TRANSFER vẫn giữ per-fund để theo dõi điều chuyển nội bộ
        from_node = { kind: 'wallet', fund_id: tx.fund_id };
        to_node   = { kind: 'wallet', fund_id: tx.dest_fund_id };
        break;
    }

    return { ...tx, from_node, to_node };
  });

  // ── Seed wallet asset nếu chưa tồn tại ──────────────────────────────────
  // Ví tiền mặt chung là tài sản đặc biệt — currentPrice = số dư thực,
  // được cập nhật trực tiếp theo từng giao dịch (không tính lại từ formula).
  const hasWalletAsset = assets.some(a => a.id === CASH_WALLET_ASSET_ID);
  const seedBalance    = hasWalletAsset ? 0 : computeWalletBalance(GLOBAL_WALLET_ID, moneyTransactions);
  const finalAssets = hasWalletAsset ? assets : [
    {
      id:           CASH_WALLET_ASSET_ID,
      name:         'Ví tiền mặt chung',
      code:         'CASH',
      assetClass:   'Tiền mặt & Tương đương',
      assetClassId: 'tien-mat',
      bucketId:     'ruong-chua',
      quantity:     1,
      // Seeded từ legacy formula — user cần DEPOSIT để điều chỉnh nếu sai
      currentPrice: seedBalance,
      costPrice:    seedBalance, // = currentPrice để gain/loss = 0 (tiền mặt không có lãi/lỗ)
      currency:     'VND' as const,
      purchaseDate: '2020-01-01',
      notes:        'Ví tiền mặt khả dụng — quản lý tự động bởi giao dịch',
    },
    ...assets,
  ];

  return {
    ...s, buckets, assets: finalAssets, moneyTransactions,
    liabilities:     s.liabilities     || [],
    repayments:      s.repayments      || [],
    monthlyExpenses: s.monthlyExpenses || [],
    cashFlowEvents:  s.cashFlowEvents  || [],
    holdings:        s.holdings        || [],
    assetClasses: (s.assetClasses && s.assetClasses.length > 0) ? s.assetClasses : DEFAULT_ASSET_CLASSES,
  };
}

// CloudLoadingScreen replaced by shared SplashScreen component

// ── Context ───────────────────────────────────────────────────────────────────
interface AppContextValue {
  state:       AppState;
  dispatch:    (action: Action) => void;
  syncing:     boolean;
  workspaceId: string | null;
}
const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [state,   dispatch] = useReducer(reducer, INITIAL_DATA);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const saveTimer   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialized = useRef(false);
  const skipSave    = useRef(false);
  const stateRef    = useRef(state);
  stateRef.current  = state; // luôn trỏ đến state mới nhất cho wrappedDispatch

  // Multi-tenancy
  const [workspaceId,      setWorkspaceId]      = useState<string | null>(null);
  const [onboardingStatus, setOnboardingStatus] = useState<'pending' | 'completed' | null>(null);

  // Splash fade-out state
  const [splashFading, setSplashFading] = useState(false);
  const [splashGone,   setSplashGone]   = useState(false);
  const splashStarted = useRef(false);

  // Wrap dispatch để xử lý side-effect DB cho một số actions
  const wrappedDispatch = React.useCallback((action: Action) => {
    if (action.type === 'DELETE_BUCKET') {
      // Xoá fund khỏi DB trực tiếp (syncFunds không còn auto-delete)
      deleteFundFromCloud(action.payload).catch(e =>
        console.error('[AppContext] deleteFundFromCloud error:', e)
      );
    }
    if (action.type === 'ADD_MONEY_TX') {
      // Insert trực tiếp vào DB — không qua bulk sync để tránh re-upload toàn bộ history
      addMoneyTransactionToCloud(action.payload).catch(e =>
        console.error('[AppContext] addMoneyTransactionToCloud error:', e)
      );
    }
    if (action.type === 'UPDATE_MONEY_TX') {
      updateMoneyTransactionInCloud(action.payload).catch(e =>
        console.error('[AppContext] updateMoneyTransactionInCloud error:', e)
      );
    }
    if (action.type === 'DELETE_MONEY_TX') {
      deleteMoneyTransactionFromCloud(action.payload).catch(e =>
        console.error('[AppContext] deleteMoneyTransactionFromCloud error:', e)
      );
    }
    if (action.type === 'ADD_HOLDING' || action.type === 'UPDATE_HOLDING') {
      saveHolding(action.payload).catch(e =>
        console.error('[AppContext] saveHolding error:', e)
      );
      // Rollup ngay lập tức để UI phản hồi mà không cần chờ DB
      dispatch({ type: 'ROLLUP_COMPOSITE_ASSET', payload: { assetId: action.payload.assetId } });
    }
    if (action.type === 'DELETE_HOLDING') {
      // Lấy assetId trước khi xóa để rollup sau
      const holdingToDelete = (stateRef.current.holdings || []).find((h: AssetHolding) => h.id === action.payload);
      deleteHolding(action.payload).catch(e =>
        console.error('[AppContext] deleteHolding error:', e)
      );
      if (holdingToDelete) {
        dispatch({ type: 'ROLLUP_COMPOSITE_ASSET', payload: { assetId: holdingToDelete.assetId } });
      }
    }
    dispatch(action);
  }, []);

  // ── Fetch từ Supabase khi mount ─────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      loadStateFromCloud(),
      getWorkspaceId(),
      getUserProfile(),
    ]).then(([cloudState, wid, profile]) => {
      if (cloudState !== null) {
        skipSave.current = true;
        dispatch({ type: 'SET_STATE', payload: migrateState(cloudState) });
      }
      setWorkspaceId(wid);
      // Nếu profile null (race condition: getWorkspaceId vừa tạo profile), mặc định 'pending'
      // để user đi qua onboarding thay vì skip sang app (an toàn hơn 'completed')
      setOnboardingStatus(profile?.onboardingStatus ?? 'pending');
      setLoading(false);
      initialized.current = true;
    });
  }, []);

  // ── Re-fetch khi tab được focus lại ─────────────────────────────────────
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && initialized.current && !skipSave.current) {
        loadStateFromCloud().then((cloudState) => {
          // null = fetch thất bại → giữ nguyên state, không xóa dữ liệu đang hiển thị
          if (cloudState !== null) {
            skipSave.current = true;
            dispatch({ type: 'SET_STATE', payload: migrateState(cloudState) });
          }
        });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // ── Debounced save → re-fetch sau khi lưu thành công ───────────────────
  useEffect(() => {
    if (!initialized.current) return;

    // Nếu state thay đổi do re-fetch (skipSave=true), không lưu lại
    if (skipSave.current) {
      skipSave.current = false;
      return;
    }

    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSyncing(true);
      try {
        const ok = await saveStateToCloud(state);
        if (ok) {
          // Re-fetch để đồng bộ state với DB truth
          const fresh = await loadStateFromCloud();

          if (fresh !== null) {
            // Giữ lại icon (app-only field, không có trong DB)
            const mergedBuckets = fresh.buckets.map(fb => {
              const cur = state.buckets.find(cb => cb.id === fb.id);
              return cur ? { ...fb, icon: cur.icon || fb.icon } : fb;
            });

            skipSave.current = true;   // ngăn vòng lặp save
            dispatch({ type: 'SET_STATE', payload: migrateState({ ...fresh, buckets: mergedBuckets }) });
          }
          // fresh === null: fetch thất bại sau khi save thành công → giữ state hiện tại
        }
        // Nếu lỗi (ok=false), lỗi đã được notify trong saveStateToCloud → không re-fetch
      } catch (e) {
        console.error('[AppContext] save/refetch exception:', e);
      } finally {
        setSyncing(false);
      }
    }, 1500);

    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // Khi data load xong, trigger fade-out cho splash trước khi reveal app
  useEffect(() => {
    if (!loading && !splashStarted.current) {
      splashStarted.current = true;
      setSplashFading(true);
      const t = setTimeout(() => setSplashGone(true), 580);
      return () => clearTimeout(t);
    }
  }, [loading]);

  // Khi onboarding chưa hoàn thành → hiện OnboardingFlow thay vì Dashboard
  const handleOnboardingComplete = React.useCallback(async (score: number) => {
    await completeOnboarding(score);
    setOnboardingStatus('completed');
  }, []);

  return (
    <AppContext.Provider value={{ state, dispatch: wrappedDispatch, syncing, workspaceId }}>
      {/* Nội dung chính — chỉ mount sau khi splash bắt đầu fade */}
      {(!loading || splashFading) && (
        onboardingStatus === 'pending'
          ? <OnboardingFlow onComplete={handleOnboardingComplete} />
          : children
      )}
      {/* Splash overlay fade-out khi data sẵn sàng */}
      {!splashGone && (
        <SplashScreen
          fading={splashFading}
          message="Đang đồng bộ hóa không gian gia sản..."
        />
      )}
    </AppContext.Provider>
  );
}

export function useAppState() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppState must be within AppProvider');
  return ctx;
}

/** Hook tiện ích để biết đang sync không */
export function useSyncing() {
  return useAppState().syncing;
}

/** Hook lấy workspace_id của user hiện tại */
export function useWorkspaceId() {
  return useAppState().workspaceId;
}
